
import { useEffect, useRef, useState } from 'react';
import { FileText } from 'lucide-react';
import { chatsApi, ticketsApi, usersApi, wikiApi, type RefHit } from '@/shared/api';
import { PersonIcon, TaskIcon, TicketIcon, ticketStatusLabel } from '@/shared/config';
import { formatRelativeTime } from '@/shared/lib/date';
import { swapLayout } from './layout';
import { matchText } from './match';
import { looksLikeTicketNumber, type Scope } from './scopes';
import { parseSnippet } from './snippet';
import { EMPTY_RESULTS, type PaletteItem, type ResultGroup, type SearchResults } from './types';

export const MIN_QUERY = 2;
const LIMIT_MIXED = 5;
const LIMIT_SCOPED = 20;

type SourcePart = { [K in ResultGroup]?: PaletteItem[] };

interface Source {
  key: string;
  groups: ResultGroup[];
  fetch: (query: string) => Promise<SourcePart>;
}

const hl = (label: string, q: string) => (q ? matchText(label, q, { fuzzy: false })?.ranges : undefined);

function refToItem(hit: RefHit, q: string): PaletteItem {
  const isTicket = hit.kind === 'ticket';
  const label = hit.title || 'Без темы';
  const statusText = isTicket ? ticketStatusLabel(hit.status, hit.statusLabel) : (hit.statusLabel ?? hit.status);
  const details: { label: string; value: string }[] = [];
  if (hit.subtitle) details.push({ label: isTicket ? 'Заявитель' : 'Исполнители', value: hit.subtitle });
  details.push({ label: 'Статус', value: statusText });
  details.push({ label: 'Обновлена', value: formatRelativeTime(hit.updatedAt) });
  return {
    id: `${isTicket ? 't' : 'k'}-${hit.id}`,
    group: isTicket ? 'tickets' : 'tasks',
    kind: isTicket ? 'ticket' : 'task',
    icon: isTicket ? TicketIcon : TaskIcon,
    label,
    ranges: hl(label, q),
    badge: hit.key,
    subtitle: hit.subtitle ?? undefined,
    status: {
      label: statusText,
      open: hit.open,
      ticketStatus: isTicket ? hit.status : undefined,
    },
    details,
    to: hit.href,
  };
}

function buildSources(opts: {
  q: string;
  scope: Scope;
  isStaff: boolean;
  canTasks: boolean;
  canWiki: boolean;
}): Source[] {
  const { q, scope, isStaff, canTasks, canWiki } = opts;
  const limit = scope === 'all' ? LIMIT_MIXED : LIMIT_SCOPED;
  const long = q.length >= MIN_QUERY || looksLikeTicketNumber(q);
  const sources: Source[] = [];

  const wantTickets = scope === 'all' || scope === 'tickets';
  const wantTasks = canTasks && (scope === 'all' || scope === 'tasks');

  if (isStaff && (wantTickets || wantTasks)) {
    const underHand = !q && (scope !== 'all' || wantTasks);
    if (long || underHand) {
      const showTickets = wantTickets && !(underHand && scope === 'all');
      const groups: ResultGroup[] = [...(showTickets ? ['tickets' as const] : []), ...(wantTasks ? ['tasks' as const] : [])];
      const take = underHand && scope === 'all' ? 4 : limit;
      sources.push({
        key: `refs:${take}:${groups.join('+')}`,
        groups,
        fetch: (query) =>
          chatsApi.searchRefs(query, take).then((res) => ({
            tickets: showTickets ? res.tickets.map((h) => refToItem(h, query)) : undefined,
            tasks: wantTasks ? res.tasks.map((h) => refToItem(h, query)) : undefined,
          })),
      });
    }
  } else if (!isStaff && wantTickets && (long || scope === 'tickets')) {
    sources.push({
      key: `mine:${limit}`,
      groups: ['tickets'],
      fetch: (query) =>
        ticketsApi.list({ q: query || undefined, limit, includeClosed: true, view: 'mine' }).then((res) => ({
          tickets: (res.data ?? []).map((t): PaletteItem => {
            const label = t.title || 'Без темы';
            const statusLabel = ticketStatusLabel(t.status, t.statusLabel);
            return {
              id: `t-${t.id}`,
              group: 'tickets',
              kind: 'ticket',
              icon: TicketIcon,
              label,
              ranges: hl(label, query),
              badge: t.number != null ? `#${t.number}` : undefined,
              status: { label: statusLabel, open: true, ticketStatus: t.status },
              details: [{ label: 'Статус', value: statusLabel }],
              to: `/tickets/${t.id}`,
            };
          }),
        })),
    });
  }

  if (canWiki && (scope === 'all' || scope === 'wiki') && q.length >= MIN_QUERY) {
    sources.push({
      key: `wiki:${limit}`,
      groups: ['wiki'],
      fetch: (query) =>
        wikiApi.documents({ query, limit }).then((res) => ({
          wiki: (res.data ?? []).slice(0, limit).map((doc): PaletteItem => {
            const details = doc.updatedAt ? [{ label: 'Обновлена', value: formatRelativeTime(doc.updatedAt) }] : [];
            return {
              id: `w-${doc.urlId}`,
              group: 'wiki',
              kind: 'wiki',
              icon: FileText,
              label: doc.title,
              ranges: hl(doc.title, query),
              snippet: doc.snippet ? parseSnippet(doc.snippet) : undefined,
              details,
              to: `/instructions/${doc.urlId}`,
            };
          }),
        })),
    });
  }

  if (isStaff && (scope === 'all' || scope === 'people') && q.length >= MIN_QUERY) {
    sources.push({
      key: `people:${limit}`,
      groups: ['people'],
      fetch: (query) =>
        usersApi.search(query).then((res) => ({
          people: (res.data ?? []).slice(0, limit).map((u): PaletteItem => {
            const details: { label: string; value: string }[] = [];
            if (u.username) details.push({ label: 'Логин', value: u.username });
            if (u.email) details.push({ label: 'Почта', value: u.email });
            return {
              id: `p-${u.id}`,
              group: 'people',
              kind: 'person',
              icon: PersonIcon,
              label: u.displayName,
              ranges: hl(u.displayName, query),
              subtitle: u.email ?? u.username ?? undefined,
              person: { seed: u.id },
              details,
              to: `/users/${u.id}`,
            };
          }),
        })),
    });
  }

  return sources;
}

export const hasRemoteSources = (opts: Parameters<typeof buildSources>[0]) => buildSources(opts).length > 0;

export const settledKey = (q: string, scope: Scope) => `${scope}|${q}`;

export interface PaletteSearch {
  results: SearchResults;
  loading: boolean;
  altQuery: string | null;
  settled: string | null;
}

export function usePaletteSearch(opts: {
  open: boolean;
  q: string;
  scope: Scope;
  isStaff: boolean;
  canTasks: boolean;
  canWiki: boolean;
}): PaletteSearch {
  const { open, q, scope, isStaff, canTasks, canWiki } = opts;
  const [results, setResults] = useState<SearchResults>(EMPTY_RESULTS);
  const [loading, setLoading] = useState(false);
  const [altQuery, setAltQuery] = useState<string | null>(null);
  const [settled, setSettled] = useState<string | null>(null);
  const seqRef = useRef(0);
  const cacheRef = useRef(new Map<string, SourcePart>());

  useEffect(() => {
    if (!open) {
      cacheRef.current.clear();
      setResults(EMPTY_RESULTS);
      setAltQuery(null);
      setLoading(false);
      setSettled(null);
      return;
    }
    const seq = ++seqRef.current;
    const cache = cacheRef.current;
    const sources = buildSources({ q, scope, isStaff, canTasks, canWiki });

    const live = new Set(sources.flatMap((s) => s.groups));
    setResults((prev) => {
      const next = { ...prev };
      for (const g of Object.keys(next) as ResultGroup[]) if (!live.has(g)) next[g] = [];
      return next;
    });
    setAltQuery(null);
    if (sources.length === 0) {
      setLoading(false);
      return;
    }

    const apply = (part: SourcePart) => {
      if (seq !== seqRef.current) return;
      const filled = Object.fromEntries(Object.entries(part).filter(([, items]) => items != null));
      setResults((prev) => ({ ...prev, ...filled }));
    };
    const countOf = (part: SourcePart) => Object.values(part).reduce((n, items) => n + (items?.length ?? 0), 0);

    const runOne = (source: Source, query: string): Promise<SourcePart> => {
      const key = `${source.key}:${query}`;
      const hit = cache.get(key);
      if (hit) return Promise.resolve(hit);
      return source
        .fetch(query)
        .then((part) => {
          cache.set(key, part);
          if (cache.size > 80) cache.delete(cache.keys().next().value as string);
          return part;
        })
        .catch(() => Object.fromEntries(source.groups.map((g) => [g, []])) as SourcePart);
    };

    const runAll = (query: string, show: boolean) =>
      Promise.all(
        sources.map((s) =>
          runOne(s, query).then((part) => {
            if (show) apply(part);
            return part;
          }),
        ),
      );

    const done = () => {
      setLoading(false);
      setSettled(settledKey(q, scope));
    };

    setLoading(true);
    void runAll(q, true).then((parts) => {
      if (seq !== seqRef.current) return;
      const total = parts.reduce((n, p) => n + countOf(p), 0);
      const swapped = total === 0 && q.length >= MIN_QUERY ? swapLayout(q) : null;
      if (!swapped) {
        done();
        return;
      }
      return runAll(swapped, false).then((alt) => {
        if (seq !== seqRef.current) return;
        if (alt.reduce((n, p) => n + countOf(p), 0) > 0) {
          alt.forEach(apply);
          setAltQuery(swapped);
        }
        done();
      });
    });
  }, [open, q, scope, isStaff, canTasks, canWiki]);

  return { results, loading, altQuery, settled };
}
